'use client';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Box, Button, ClickAwayListener, Paper, Popper, Stack, Typography } from '@mui/material';
import { startTransition, useEffect, useRef, useState } from 'react';
import { useShallow } from 'zustand/shallow';
import { useHYSACalculatorStore } from '../calculatorStore';
import { TimePeriodDropdownOptions } from './TimePeriodDropdownOptions';

const TIME_PERIODS = [
  { value: 1, label: '1 year' },
  { value: 3, label: '3 years' },
  { value: 5, label: '5 years' },
  { value: 10, label: '10 years' },
  { value: 15, label: '15 years' },
  { value: 20, label: '20 years' },
];

const popperSameWidthModifiers = [
  {
    name: 'sameWidth',
    enabled: true,
    phase: 'beforeWrite' as const,
    fn: ({
      state,
    }: {
      state: {
        rects: { reference: { width: number } };
        styles: { popper?: Record<string, string> };
      };
    }) => {
      const width = `${state.rects.reference.width}px`;
      const popperStyle = state.styles.popper ?? {};
      popperStyle.minWidth = width;
      popperStyle.width = width;
    },
  },
];

export function TimePeriodForm() {
  const { isOpened, timePeriod, handleTimePeriodOpen } = useHYSACalculatorStore(
    useShallow(state => ({
      isOpened: state.isTimePeriodOpen,
      timePeriod: state.formFields.timePeriod,
      handleTimePeriodOpen: state.handleTimePeriodOpen,
    })),
  );
  const buttonAnchor = useRef<HTMLButtonElement | null>(null);

  const [activeIndex, setActiveIndex] = useState(() => {
    const index = TIME_PERIODS.findIndex(time => time.value.toString() === timePeriod);
    return index === -1 ? 0 : index;
  });

  const closeDropdown = () => {
    startTransition(() => {
      buttonAnchor.current?.focus();
      handleTimePeriodOpen('timePeriod', timePeriod, !isOpened);
    });
  };

  useEffect(() => {
    const closeOnEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeDropdown();
      }
    };
    if (isOpened) window.addEventListener('keydown', closeOnEsc);
    return () => window.removeEventListener('keydown', closeOnEsc);
  }, [isOpened]);

  const moveOnList = (directionMove: 1 | -1) =>
    setActiveIndex(index => (index + directionMove + TIME_PERIODS.length) % TIME_PERIODS.length);

  const onKeyList = (e: React.KeyboardEvent) => {
    e.preventDefault();
    switch (e.key) {
      case 'ArrowDown':
        moveOnList(1);
        break;
      case 'ArrowUp':
        moveOnList(-1);
        break;
      case 'Home':
        setActiveIndex(0);
        break;
      case 'End':
        setActiveIndex(TIME_PERIODS.length - 1);
        break;
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '0.125rem',
      }}
    >
      <Typography component="label" variant="textSmallBold" htmlFor="time-period">
        Time period
      </Typography>
      <Box
        sx={{
          borderRadius: '8px',
          display: 'flex',
          flexDirection: 'row',
          position: 'relative',
        }}
      >
        <Button
          aria-activedescendant={isOpened ? `option-${TIME_PERIODS[activeIndex].value}` : undefined}
          aria-haspopup="listbox"
          disableRipple
          id="time-period"
          onClick={() => {
            handleTimePeriodOpen('timePeriod', timePeriod, !isOpened);
          }}
          ref={buttonAnchor}
          role="combobox"
          sx={{
            borderColor: 'grey.200',
            borderStyle: 'solid',
            borderRadius: '8px',
            borderWidth: '1px',
            display: 'flex',
            gap: '.125rem',
            justifyContent: 'left',
            padding: 0,
            paddingX: '1rem',
            paddingY: '0.5rem',
            width: '100%',
            '&:hover': {
              borderColor: 'blues.light',
            },
            '&.Mui-focusVisible': {
              outlineColor: 'blues.light',
            },
          }}
          variant="outlined"
        >
          <Typography
            data-testid="time-period-value-label"
            variant="text"
            sx={{
              color: 'common.black',
              fontWeight: 325,
              textTransform: 'none',
            }}
          >
            {TIME_PERIODS.find(time => time.value.toString() === timePeriod)?.label}
          </Typography>
        </Button>
        <Box
          sx={{
            position: 'absolute',
            right: '1rem',
            top: '55%',
            transform: 'translateY(-50%)',
            pointerEvents: 'none',
          }}
        >
          {isOpened ? (
            <KeyboardArrowUpIcon sx={{ width: '1.5rem', height: '1.5rem' }} />
          ) : (
            <KeyboardArrowDownIcon sx={{ width: '1.5rem', height: '1.5rem' }} />
          )}
        </Box>
        <Popper
          anchorEl={buttonAnchor.current}
          modifiers={popperSameWidthModifiers}
          popperOptions={{ strategy: 'fixed' }}
          open={isOpened}
          placement="bottom-start"
          sx={{
            zIndex: 200,
          }}
        >
          <ClickAwayListener
            onClickAway={() => {
              handleTimePeriodOpen('timePeriod', timePeriod, !isOpened);
            }}
          >
            <Paper
              square
              elevation={3}
              sx={{
                borderRadius: '8px',
                marginTop: 2,
                width: '100%',
              }}
            >
              <Stack
                id="time-period-list"
                role="listbox"
                aria-labelledby="time-period"
                tabIndex={0}
                sx={{
                  py: 0.5,
                  width: '100%',
                }}
                onKeyDown={onKeyList}
              >
                <TimePeriodDropdownOptions activeIndex={activeIndex} options={TIME_PERIODS} />
              </Stack>
            </Paper>
          </ClickAwayListener>
        </Popper>
      </Box>
    </Box>
  );
}
